import { create } from 'zustand'
import type { BancoDoParceiro, ConfiguracaoParceiro } from '@/types'
import * as parceirosApi from '@/api/parceiros'
import { getApiErrorMessage } from '@/lib/apiError'

interface ConfiguracaoState {
  banco: BancoDoParceiro
  payeeCode: string | null
  numeroConta: string | null
  aceitaCartao: boolean
  logoUrl: string | null
  hasContaHyperpay: boolean
  carregado: boolean
  loading: boolean
  saving: boolean
  savingCartao: boolean
  savingLogo: boolean
  savingBanco: boolean
  error: string | null
  fetchConfig: () => Promise<void>
  salvarDadosDeRepasse: (values: { payeeCode: string; numeroConta: string }) => Promise<void>
  updateBanco: (banco: BancoDoParceiro) => Promise<void>
  updateAceitaCartao: (aceitaCartao: boolean) => Promise<void>
  uploadLogo: (file: File) => Promise<void>
  removerLogo: () => Promise<void>
}

export const useConfiguracaoStore = create<ConfiguracaoState>((set) => ({
  banco: 'EFI',
  payeeCode: null,
  numeroConta: null,
  aceitaCartao: false,
  logoUrl: null,
  hasContaHyperpay: false,
  carregado: false,
  loading: false,
  saving: false,
  savingCartao: false,
  savingLogo: false,
  savingBanco: false,
  error: null,

  fetchConfig: async () => {
    set({ loading: true, error: null })
    try {
      const config: ConfiguracaoParceiro = await parceirosApi.getConfig()
      set({
        banco: config.banco ?? 'EFI',
        payeeCode: config.payeeCode,
        numeroConta: config.numeroConta,
        aceitaCartao: config.aceitaCartao,
        logoUrl: config.logoUrl ?? null,
        hasContaHyperpay: config.hasContaHyperpay ?? false,
        carregado: true,
        loading: false,
      })
    } catch {
      // `carregado` fica true mesmo no erro: sem isso o Layout tentaria de novo a cada render.
      // O modo permanece false — falhar fechado aqui significa NÃO esconder a matrícula, que é o
      // certo, porque quem bloqueia de verdade é o backend (403), não esta tela.
      set({ error: 'Erro ao carregar configurações', carregado: true, loading: false })
    }
  },

  // O backend emite um PIX e um boleto de teste antes de gravar, então o erro que volta diz qual
  // dos dois campos a Efí recusou — repassá-lo é a única coisa útil que a tela pode mostrar.
  salvarDadosDeRepasse: async (values) => {
    set({ saving: true, error: null })
    try {
      const salvo = await parceirosApi.salvarDadosDeRepasse(values)
      set({ payeeCode: salvo.payeeCode, numeroConta: salvo.numeroConta, saving: false })
    } catch (error) {
      const message = getApiErrorMessage(error, 'Erro ao salvar os dados de repasse')
      set({ error: message, saving: false })
      throw new Error(message)
    }
  },

  updateBanco: async (banco) => {
    set({ savingBanco: true, error: null })
    try {
      await parceirosApi.updateConfig({ banco })
      set({ banco, savingBanco: false })
    } catch (error) {
      const message = getApiErrorMessage(error, 'Erro ao salvar o banco')
      set({ error: message, savingBanco: false })
      throw new Error(message)
    }
  },

  updateAceitaCartao: async (aceitaCartao) => {
    set({ savingCartao: true, error: null })
    try {
      await parceirosApi.updateConfig({ aceitaCartao })
      set({ aceitaCartao, savingCartao: false })
    } catch {
      set({ error: 'Erro ao salvar configuração de cartão', savingCartao: false })
      throw new Error('Erro ao salvar configuração de cartão')
    }
  },

  // Rethrows the backend's message (tipo/tamanho recusados) so the card can show the real reason.
  uploadLogo: async (file) => {
    set({ savingLogo: true, error: null })
    try {
      const { logoUrl } = await parceirosApi.uploadLogo(file)
      set({ logoUrl, savingLogo: false })
    } catch (error) {
      const message = getApiErrorMessage(error, 'Erro ao enviar a logo')
      set({ error: message, savingLogo: false })
      throw new Error(message)
    }
  },

  removerLogo: async () => {
    set({ savingLogo: true, error: null })
    try {
      await parceirosApi.removerLogo()
      set({ logoUrl: null, savingLogo: false })
    } catch (error) {
      const message = getApiErrorMessage(error, 'Erro ao remover a logo')
      set({ error: message, savingLogo: false })
      throw new Error(message)
    }
  },
}))
